import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Typography
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75, 1.5),
      gap: theme.spacing(0.75)
    }
  },
  rootWithCredits: {
    borderColor: theme.palette.success.main,
    backgroundColor: `${theme.palette.success.main}08`
  },
  rootNoCredits: {
    borderColor: theme.palette.warning.main,
    backgroundColor: `${theme.palette.warning.main}08`
  },
  icon: {
    color: theme.palette.primary.main,
    fontSize: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: 18
    }
  },
  iconWarning: {
    color: theme.palette.warning.main
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    minWidth: 120,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100
    }
  },
  creditsText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem'
    }
  },
  planName: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem'
    }
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    marginTop: theme.spacing(0.5)
  },
  buyButton: {
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.4, 1.2),
      fontSize: '0.7rem'
    }
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  loadingText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  }
}));

const BundleStatusBadge = ({
  bundleStatus,
  loading,
  error,
  showPlanName,
  showProgress
}) => {
  const classes = useStyles();

  // Loading state
  if (loading) {
    return (
      <Box className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={16} thickness={4} />
          <Typography className={classes.loadingText}>
            Loading credits...
          </Typography>
        </div>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Tooltip title="Unable to load credits. Click to retry.">
        <Box className={classes.root}>
          <CreditCardIcon
            className={`${classes.icon} ${classes.iconWarning}`}
          />
          <Typography
            className={classes.creditsText}
            style={{ color: '#f57c00' }}>
            Unable to load
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  // No active bundle or 0 credits
  if (!bundleStatus.hasActiveBundle || bundleStatus.remainingCredits === 0) {
    return (
      <Box className={`${classes.root} ${classes.rootNoCredits}`}>
        <ShoppingCartIcon
          className={`${classes.icon} ${classes.iconWarning}`}
        />
        <div className={classes.content}>
          <Typography className={classes.creditsText}>
            {bundleStatus.remainingCredits === 0
              ? '0 jobs remaining'
              : 'No jobs'}
          </Typography>
        </div>
      </Box>
    );
  }

  // Active bundle with credits
  const { remainingCredits, totalCredits, planName } = bundleStatus;
  const progressPercentage = (remainingCredits / totalCredits) * 100;
  const isLowCredits = remainingCredits <= 2;

  return (
    <Tooltip
      title={
        <Box>
          {showPlanName && (
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {planName}
            </Typography>
          )}
          <Typography variant="caption">
            {remainingCredits} of {totalCredits} jobs remaining
          </Typography>
          {isLowCredits && (
            <Typography
              variant="caption"
              style={{ display: 'block', marginTop: 4, color: '#ffb74d' }}>
              Running low! Consider purchasing more jobs.
            </Typography>
          )}
        </Box>
      }>
      <Box className={`${classes.root} ${classes.rootWithCredits}`}>
        <CreditCardIcon className={classes.icon} />
        <div className={classes.content}>
          <Typography className={classes.creditsText}>
            {remainingCredits} {remainingCredits === 1 ? 'job' : 'jobs'}
          </Typography>
          {showPlanName && (
            <Typography className={classes.planName}>{planName}</Typography>
          )}
          {showProgress && (
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              className={classes.progressBar}
              color={isLowCredits ? 'secondary' : 'primary'}
            />
          )}
        </div>
      </Box>
    </Tooltip>
  );
};

BundleStatusBadge.propTypes = {
  bundleStatus: PropTypes.shape({
    hasActiveBundle: PropTypes.bool,
    planName: PropTypes.string,
    totalCredits: PropTypes.number,
    remainingCredits: PropTypes.number,
    createdAt: PropTypes.string
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  showPlanName: PropTypes.bool,
  showProgress: PropTypes.bool
};

BundleStatusBadge.defaultProps = {
  loading: false,
  error: null,
  showPlanName: true,
  showProgress: true
};

export default BundleStatusBadge;
